// List ⇄ detail navigation helpers.
//
// Problem they solve: a paginated/filtered list page kept its page and
// filters in component state, so opening a row's detail page and coming back
// (via the "← Back to …" link or the browser) always landed on page 1 with
// the filters reset, and the row you came from was lost in the crowd.
//
// The pieces:
//   useUrlState / useUrlNumberState / useUrlBoolState — drop-in `useState`
//     replacements whose value lives in the URL query string (?page=3&q=acme),
//     written with `replace` so paging doesn't flood history. Defaults are
//     omitted from the URL to keep it clean. Because the state is in the URL,
//     browser back/forward and the Back link both restore it for free.
//   useUpdateEffect — `useEffect` that skips the mount run; used for the
//     "reset to page 1 when a filter changes" effects, which must NOT fire on
//     mount or they'd clobber the page restored from the URL.
//   rowLinkState — what a row's <Link> passes as `state`: where it came from.
//   useBackTo — what a detail page feeds PageHeader's `backTo`: returns to the
//     exact list URL recorded in `state.from` (falling back to the default
//     list), labelled by that origin, and tells the list which row to highlight.
//   useHighlightedRow — on the list page: which row id to highlight (from
//     `location.state.highlight`), scrolled into view once, cleared after a
//     few seconds.
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

type Updater<T> = T | ((prev: T) => T);

function resolve<T>(next: Updater<T>, prev: T): T {
  return typeof next === "function" ? (next as (p: T) => T)(prev) : next;
}

/** String state mirrored in `?key=`. `defaultValue` is represented by the key being absent. */
// `NoInfer` keeps T from being inferred as the default's literal type (`useUrlState("q", "")`
// would otherwise yield a state that can only ever hold ""); pass an explicit union to narrow.
export function useUrlState<T extends string = string>(key: string, defaultValue: NoInfer<T>): [T, Dispatch<SetStateAction<T>>] {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const raw = searchParams.get(key);
  const value = (raw === null ? defaultValue : raw) as T;
  const ref = useRef(value);
  ref.current = value;
  const stateRef = useRef(location.state);
  stateRef.current = location.state;
  const set = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    const v = resolve(next, ref.current);
    // No-op writes (e.g. a debounce effect echoing the mount value) must not
    // touch history at all.
    if (v === ref.current) return;
    ref.current = v;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (v === defaultValue) p.delete(key); else p.set(key, v);
      return p;
    // `replace` keeps paging out of the history stack; carrying `state`
    // through keeps the row-highlight hint alive across URL writes.
    }, { replace: true, state: stateRef.current });
  }, [key, defaultValue, setSearchParams]);
  return [value, set];
}

/** Integer state mirrored in `?key=`; anything unparseable reads as the default. */
export function useUrlNumberState(key: string, defaultValue: number): [number, Dispatch<SetStateAction<number>>] {
  const [raw, setRaw] = useUrlState(key, String(defaultValue));
  const n = Number.parseInt(raw, 10);
  const value = Number.isFinite(n) && n >= 1 ? n : defaultValue;
  const ref = useRef(value);
  ref.current = value;
  const set = useCallback<Dispatch<SetStateAction<number>>>((next) => {
    setRaw(String(resolve(next, ref.current)));
  }, [setRaw]);
  return [value, set];
}

/** Boolean state mirrored as `?key=1` / `?key=0`. The default (`false` unless given) is the key being
 *  absent, so a default-on filter stays distinguishable from "explicitly turned off" (`?key=0`). */
export function useUrlBoolState(key: string, defaultValue = false): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [raw, setRaw] = useUrlState(key, defaultValue ? "1" : "");
  const value = raw === "1";
  const ref = useRef(value);
  ref.current = value;
  const set = useCallback<Dispatch<SetStateAction<boolean>>>((next) => {
    const v = resolve(next, ref.current);
    setRaw(v ? "1" : defaultValue ? "0" : "");
  }, [setRaw, defaultValue]);
  return [value, set];
}

/** `useEffect` that does not run on mount — only when `deps` change afterwards. */
export function useUpdateEffect(effect: () => void | (() => void), deps: unknown[]) {
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    return effect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export interface ListLinkState {
  /** The full list URL (path + query) to return to. */
  from: string;
  /**
   * The origin page's OWN router state, restored when we come back to it —
   * so a Company page opened from "Companies page 2" still returns to page 2
   * after a round trip to one of its contacts (nesting handles any depth).
   */
  fromState?: unknown;
}
export interface BackLinkState {
  /** Row id the list should highlight when we land back on it. */
  highlight: number | string;
}

/** `state` for a row's <Link> to its detail page: remembers the exact list URL. */
export function rowLinkState(location: { pathname: string; search: string; state?: unknown }): ListLinkState {
  const from = `${location.pathname}${location.search}`;
  return location.state ? { from, fromState: location.state } : { from };
}

/** Human label for the page a `from` URL points at (falls back to the default). */
export function backLabelFor(from: string | undefined, fallback: string): string {
  if (!from) return fallback;
  const path = from.split("?")[0];
  if (/\/crm\/companies\/\d+$/.test(path)) return "Back to Company";
  if (/\/crm\/companies$/.test(path)) return "Back to Companies";
  if (/\/crm\/prime-contacts$/.test(path)) return "Back to Prime Contacts";
  if (/\/crm\/prime-contacts\/review-queue$/.test(path)) return "Back to Email Review Queue";
  if (/\/crm\/prime-contacts\/\d+$/.test(path)) return "Back to Contact";
  if (/\/crm$/.test(path)) return "Back to Pipeline";
  return fallback;
}

/**
 * For a detail page: the `backTo` to hand PageHeader. Returns to the list URL
 * the user came from (or `defaultTo`), and asks that list to highlight `ownId`.
 */
export function useBackTo(defaultTo: string, defaultLabel: string, ownId: number | string | undefined) {
  const location = useLocation();
  const incoming = location.state as Partial<ListLinkState> | null;
  const from = incoming?.from;
  const to = from && from.startsWith("/") ? from : defaultTo;
  // Give the origin page back its own state (its own `from`, …) plus the row to highlight.
  const restored = incoming?.fromState && typeof incoming.fromState === "object" ? (incoming.fromState as Record<string, unknown>) : {};
  const state: (Partial<BackLinkState> & Record<string, unknown>) | undefined =
    ownId !== undefined ? { ...restored, highlight: ownId } : Object.keys(restored).length ? restored : undefined;
  return { to, label: backLabelFor(from, defaultLabel), state };
}

/** Classes for a highlighted table row (fades back to normal via transition). */
export const ROW_HIGHLIGHT_CLASS = "ring-2 ring-inset ring-primary bg-primary-soft/70";

/**
 * For a list page: the row id to highlight after returning from a detail
 * page, or null. Scrolls the row (`id={rowDomId(prefix, id)}`) into view once
 * it exists, and clears the highlight after `ms`.
 */
export function useHighlightedRow(prefix: string, ready: boolean, ms = 4000): number | string | null {
  const location = useLocation();
  const incoming = (location.state as Partial<BackLinkState> | null)?.highlight ?? null;
  const [highlight, setHighlight] = useState<number | string | null>(incoming);
  const scrolled = useRef<string | null>(null);

  useEffect(() => { setHighlight(incoming); }, [incoming]);

  // The fade timer starts once the rows are on screen (not at mount), so a
  // slow fetch can't eat the highlight before the user ever sees it.
  useEffect(() => {
    if (highlight === null || !ready) return;
    const t = setTimeout(() => setHighlight(null), ms);
    return () => clearTimeout(t);
  }, [highlight, ready, ms]);

  useEffect(() => {
    if (highlight === null || !ready) return;
    const domId = rowDomId(prefix, highlight);
    if (scrolled.current === domId) return;
    const el = document.getElementById(domId);
    if (el) { scrolled.current = domId; el.scrollIntoView({ block: "center", behavior: "smooth" }); }
  }, [highlight, ready, prefix]);

  return highlight;
}

export function rowDomId(prefix: string, id: number | string): string {
  return `${prefix}-${id}`;
}
